import { CloseIcon } from "@/components/icons";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useQuery } from "@tanstack/react-query";
import { useId, useState, type KeyboardEvent } from "react";

import {
  ALFRED_KEY,
  personName,
  searchCcUsers,
  type AlfredCcPerson,
} from "./alfred.ts";

/** El tope de `text` en `search_alfred_users`. */
const CC_SEARCH_MAX = 100;

export const CC_TEXTS = {
  label: "Con copia a",
  placeholder: "Busca por nombre o correo",
  help: "Quien va en copia ve el pedido y sus novedades, sin poder escribir. Enter agrega la primera coincidencia; Backspace con el campo vacío quita la última.",
  none: "Nadie en copia.",
  chosen: "Personas en copia",
  remove: (name: string) => `Quitar a ${name} de la copia`,
  results: "Usuarios para la copia",
  noResults: "Nadie coincide.",
  add: (name: string) => `Agregar a ${name}`,
} as const;

/**
 * «Con copia a»: las personas elegidas, como chips que se quitan, y un
 * buscador de usuarios. Todo con teclado: Enter agrega la primera
 * coincidencia y Backspace con el campo vacío quita la última. Es
 * controlado: quien lo usa decide cuándo se guarda.
 */
export function CcPicker({
  value,
  onChange,
  disabled,
  inputRef,
}: {
  value: readonly AlfredCcPerson[];
  onChange: (next: readonly AlfredCcPerson[]) => void;
  disabled?: boolean | undefined;
  inputRef?: React.Ref<HTMLInputElement> | undefined;
}) {
  const id = useId();
  const [search, setSearch] = useState("");
  const text = search.trim();
  const users = useQuery({
    queryKey: [...ALFRED_KEY, "cc-users", text],
    queryFn: () => searchCcUsers(text),
    enabled: text.length >= 2,
    staleTime: 30_000,
  });
  const chosen = new Set(value.map((person) => person.userId));
  const matches = (users.data?.users ?? []).filter(
    (person) => !chosen.has(person.userId),
  );

  const add = (person: AlfredCcPerson) => {
    onChange([...value, person]);
    setSearch("");
  };
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      /* Ctrl+Enter es del formulario (enviar). */
      if (event.ctrlKey || event.metaKey) return;
      event.preventDefault();
      const first = matches[0];
      if (first !== undefined) add(first);
    } else if (event.key === "Backspace" && search === "") {
      if (value.length > 0) onChange(value.slice(0, -1));
    }
  };

  return (
    <div className="grid gap-1.5">
      <Label htmlFor={`${id}-search`}>{CC_TEXTS.label}</Label>
      {value.length === 0 ? (
        <p className="text-xs text-muted-foreground">{CC_TEXTS.none}</p>
      ) : (
        <ul className="flex flex-wrap gap-1.5" aria-label={CC_TEXTS.chosen}>
          {value.map((person) => (
            <li
              key={person.userId}
              className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs"
            >
              {personName(person)}
              <button
                type="button"
                disabled={disabled}
                aria-label={CC_TEXTS.remove(personName(person))}
                className="rounded-full hover:bg-secondary"
                onClick={() => {
                  onChange(value.filter((p) => p.userId !== person.userId));
                }}
              >
                <CloseIcon className="size-3" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}
      <Input
        id={`${id}-search`}
        maxLength={CC_SEARCH_MAX}
        ref={inputRef}
        disabled={disabled}
        autoComplete="off"
        placeholder={CC_TEXTS.placeholder}
        value={search}
        onChange={(event) => {
          setSearch(event.target.value);
        }}
        onKeyDown={onKeyDown}
      />
      {text.length >= 2 && users.isSuccess && (
        <ul aria-label={CC_TEXTS.results} className="grid gap-0.5">
          {matches.length === 0 ? (
            <li className="text-xs text-muted-foreground">
              {CC_TEXTS.noResults}
            </li>
          ) : (
            matches.map((person) => (
              <li key={person.userId}>
                <button
                  type="button"
                  aria-label={CC_TEXTS.add(personName(person))}
                  className="w-full rounded px-2 py-1 text-left text-sm hover:bg-secondary"
                  onClick={() => {
                    add(person);
                  }}
                >
                  {personName(person)}
                  {person.email ? (
                    <span className="ml-2 text-xs text-muted-foreground">
                      {person.email}
                    </span>
                  ) : null}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
      <p className="text-xs text-muted-foreground">{CC_TEXTS.help}</p>
    </div>
  );
}
